import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Loader2, Mail, Phone, MapPin, ShoppingBag, Calendar, Shield, Ban, CheckCircle2, Truck, Package, XCircle, User as UserIcon, Map as MapIcon, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../lib/api';
import { UserAvatar } from './VapeAvatars';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const statusMap = {
  'Entregado': { icon: CheckCircle2, color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' },
  'En camino': { icon: Truck, color: 'text-orange-400 bg-orange-500/10 border-orange-500/30' },
  'Preparando': { icon: Package, color: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/30' },
  'Cancelado': { icon: XCircle, color: 'text-rose-400 bg-rose-500/10 border-rose-500/30' },
};

const InlineMap = ({ lat, lng }) => (
  <div className="rounded-xl overflow-hidden border border-white/10" style={{ height: 240 }}>
    <MapContainer center={[lat, lng]} zoom={15} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
      <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
      <Marker position={[lat, lng]} />
    </MapContainer>
  </div>
);

const AddressBlock = ({ addr }) => {
  const [open, setOpen] = useState(false);
  if (!addr) return (
    <div className="text-white/50 text-sm bg-white/5 border border-white/10 rounded-xl p-4 text-center">
      Este usuario no ha registrado una dirección.
    </div>
  );
  const fields = [
    { label: 'CP', v: addr.cp || addr.zip },
    { label: 'Colonia', v: addr.colonia },
    { label: 'Calle', v: addr.calle },
    { label: 'No. Exterior', v: addr.ext },
    { label: 'No. Interior', v: addr.interior },
    { label: 'Entre calle', v: addr.entre_calle },
    { label: 'Y calle', v: addr.y_calle },
    { label: 'Referencia', v: addr.referencia },
    { label: 'Alias', v: addr.alias },
  ].filter((f) => f.v);

  return (
    <div className="space-y-3">
      {addr.full && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-3 flex items-start gap-2">
          <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-orange-400" />
          <p className="text-white/80 text-sm">{addr.full}</p>
        </div>
      )}

      {fields.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {fields.map((f) => (
            <div key={f.label} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2">
              <p className="text-white/50 text-[10px] uppercase tracking-wide">{f.label}</p>
              <p className="text-white text-sm font-semibold truncate">{f.v}</p>
            </div>
          ))}
        </div>
      )}

      {addr.lat && addr.lng && (
        <>
          <button
            onClick={() => setOpen((v) => !v)}
            className="w-full h-11 rounded-xl bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 text-orange-300 font-semibold text-sm inline-flex items-center justify-center gap-2 transition"
          >
            <MapIcon className="w-4 h-4" />
            {open ? 'Ocultar mapa' : 'Ver en el mapa'}
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {open && <InlineMap lat={addr.lat} lng={addr.lng} />}
        </>
      )}
    </div>
  );
};

const UserProfileDialog = ({ userId, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    api.get(`/admin/users/${userId}/full`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, userId]);

  const u = data?.user;
  const orders = data?.orders || [];
  const stats = data?.stats || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-neutral-950 border-white/10 text-white max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">Perfil de usuario</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-orange-500 inline" /></div>
        ) : u ? (
          <div className="space-y-4">
            {/* Header card */}
            <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10">
              <UserAvatar picture={u.picture} name={u.name} phone={u.phone} email={u.email} size={64} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-white font-black text-lg truncate">{u.name || 'Sin nombre'} {u.last_name || ''}</h3>
                  {u.is_admin && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-400 border border-orange-500/30"><Shield className="w-3 h-3 inline -mt-0.5 mr-0.5" /> Admin</span>}
                  {u.blocked && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-400 border border-rose-500/30"><Ban className="w-3 h-3 inline -mt-0.5 mr-0.5" /> Bloqueado</span>}
                </div>
                <div className="mt-1 text-sm text-white/70 space-y-0.5">
                  {u.email && <div className="flex items-center gap-1.5 truncate"><Mail className="w-3.5 h-3.5" /> {u.email}</div>}
                  {u.phone && <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {u.phone}</div>}
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                <p className="text-white/50 text-xs">Pedidos</p>
                <p className="text-2xl font-black text-white">{stats.orders_count || 0}</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                <p className="text-white/50 text-xs">Total gastado</p>
                <p className="text-2xl font-black text-white">${(stats.total_spent || 0).toLocaleString('es-MX')}</p>
              </div>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="perfil" className="w-full">
              <TabsList className="bg-white/5 border border-white/10 grid grid-cols-3 w-full h-11">
                <TabsTrigger value="perfil" className="data-[state=active]:bg-orange-500 data-[state=active]:text-black text-white/70"><UserIcon className="w-4 h-4 mr-1" /> Datos</TabsTrigger>
                <TabsTrigger value="direccion" className="data-[state=active]:bg-orange-500 data-[state=active]:text-black text-white/70"><MapPin className="w-4 h-4 mr-1" /> Dirección</TabsTrigger>
                <TabsTrigger value="pedidos" className="data-[state=active]:bg-orange-500 data-[state=active]:text-black text-white/70"><ShoppingBag className="w-4 h-4 mr-1" /> Pedidos</TabsTrigger>
              </TabsList>

              <TabsContent value="perfil" className="mt-3">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'Nombre', v: u.name },
                    { label: 'Apellido', v: u.last_name },
                    { label: 'Correo', v: u.email },
                    { label: 'Teléfono', v: u.phone },
                    { label: 'Fecha nacimiento', v: u.birthdate },
                    { label: 'CP registrado', v: u.coverage_zip },
                  ].filter((f) => f.v).map((f) => (
                    <div key={f.label} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                      <p className="text-white/50 text-[10px] uppercase tracking-wide">{f.label}</p>
                      <p className="text-white text-sm font-semibold truncate">{f.v}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="direccion" className="mt-3">
                <AddressBlock addr={u.address} />
              </TabsContent>

              <TabsContent value="pedidos" className="mt-3">
                {orders.length === 0 ? (
                  <p className="text-white/50 text-sm bg-white/5 border border-white/10 rounded-xl p-4 text-center">Sin pedidos aún.</p>
                ) : (
                  <ul className="space-y-2">
                    {orders.map((o) => {
                      const s = statusMap[o.status] || statusMap['Preparando'];
                      const Icon = s.icon;
                      return (
                        <li key={o.order_id} className="bg-white/5 border border-white/10 rounded-xl p-3">
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <p className="text-white font-mono text-sm">{o.order_id}</p>
                              <p className="text-white/50 text-xs">{new Date(o.created_at).toLocaleString('es-MX')} · {o.items?.length || 0} art.</p>
                            </div>
                            <div className="text-right">
                              <p className="text-white font-bold">${o.total?.toLocaleString('es-MX')}</p>
                              <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${s.color}`}>
                                <Icon className="w-3 h-3" /> {o.status}
                              </span>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <p className="text-white/50 text-center py-8">Usuario no encontrado</p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserProfileDialog;
